import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaCloud,
  FaCloudRain,
  FaCloudShowersHeavy,
  FaCloudSun,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
  FaSnowflake,
  FaSun,
  FaTemperatureHigh,
  FaTint,
  FaWind,
} from "react-icons/fa";

import Reveal from "./ui/Reveal";
import { getPublicWeather } from "../services/weatherServices";

const CACHE_KEY = "monapplimairie_public_weather_cache_v1";
const HIDDEN_KEY = "monapplimairie_weather_panel_hidden";

const safeStr = (value) => (typeof value === "string" ? value.trim() : "");

const getEnv = (key, fallback = "") => {
  const value = process.env?.[key];
  return value !== undefined && value !== null ? value : fallback;
};

const WEATHER_META = {
  0: {
    label: "Ciel dégagé",
    icon: FaSun,
    tone: "text-amber-500",
    bg: "bg-amber-500/10",
  },
  1: {
    label: "Principalement dégagé",
    icon: FaCloudSun,
    tone: "text-amber-500",
    bg: "bg-amber-500/10",
  },
  2: {
    label: "Partiellement nuageux",
    icon: FaCloudSun,
    tone: "text-sky-500",
    bg: "bg-sky-500/10",
  },
  3: {
    label: "Couvert",
    icon: FaCloud,
    tone: "text-slate-500",
    bg: "bg-slate-500/10",
  },
  45: {
    label: "Brouillard",
    icon: FaCloud,
    tone: "text-slate-500",
    bg: "bg-slate-500/10",
  },
  48: {
    label: "Brouillard givrant",
    icon: FaCloud,
    tone: "text-slate-500",
    bg: "bg-slate-500/10",
  },
  51: {
    label: "Bruine légère",
    icon: FaCloudRain,
    tone: "text-sky-600",
    bg: "bg-sky-500/10",
  },
  53: {
    label: "Bruine modérée",
    icon: FaCloudRain,
    tone: "text-sky-600",
    bg: "bg-sky-500/10",
  },
  55: {
    label: "Bruine dense",
    icon: FaCloudRain,
    tone: "text-sky-700",
    bg: "bg-sky-500/10",
  },
  61: {
    label: "Pluie faible",
    icon: FaCloudRain,
    tone: "text-sky-600",
    bg: "bg-sky-500/10",
  },
  63: {
    label: "Pluie modérée",
    icon: FaCloudRain,
    tone: "text-sky-700",
    bg: "bg-sky-500/10",
  },
  65: {
    label: "Forte pluie",
    icon: FaCloudShowersHeavy,
    tone: "text-blue-700",
    bg: "bg-blue-500/10",
  },
  71: {
    label: "Neige faible",
    icon: FaSnowflake,
    tone: "text-cyan-600",
    bg: "bg-cyan-500/10",
  },
  73: {
    label: "Neige modérée",
    icon: FaSnowflake,
    tone: "text-cyan-700",
    bg: "bg-cyan-500/10",
  },
  75: {
    label: "Forte neige",
    icon: FaSnowflake,
    tone: "text-cyan-800",
    bg: "bg-cyan-500/10",
  },
  80: {
    label: "Averses faibles",
    icon: FaCloudRain,
    tone: "text-sky-600",
    bg: "bg-sky-500/10",
  },
  81: {
    label: "Averses modérées",
    icon: FaCloudRain,
    tone: "text-sky-700",
    bg: "bg-sky-500/10",
  },
  82: {
    label: "Fortes averses",
    icon: FaCloudShowersHeavy,
    tone: "text-blue-700",
    bg: "bg-blue-500/10",
  },
  95: {
    label: "Orage",
    icon: FaCloudShowersHeavy,
    tone: "text-violet-700",
    bg: "bg-violet-500/10",
  },
  96: {
    label: "Orage avec grêle",
    icon: FaCloudShowersHeavy,
    tone: "text-violet-700",
    bg: "bg-violet-500/10",
  },
  99: {
    label: "Orage violent",
    icon: FaCloudShowersHeavy,
    tone: "text-rose-700",
    bg: "bg-rose-500/10",
  },
};

const formatNumber = (value, digits = 0) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return "—";

  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(number);
};

const getCachedWeather = (maxAgeMinutes) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);

    if (!createdAt || !parsed?.data) return null;

    const maxAgeMs = Math.max(1, Number(maxAgeMinutes) || 20) * 60 * 1000;
    const isFresh = Date.now() - createdAt < maxAgeMs;

    return isFresh ? parsed.data : null;
  } catch {
    return null;
  }
};

const setCachedWeather = (data) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        createdAt: Date.now(),
        data,
      }),
    );
  } catch {
    // LocalStorage indisponible : on ignore.
  }
};

function WeatherMetric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-sky-500/10 text-sky-600 dark:text-sky-300">
          <Icon className="h-4 w-4" />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-base font-bold text-slate-900 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function HiddenWeatherButton({ city, onShow }) {
  return (
    <section className="relative bg-slate-50 px-4 py-4 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-[1380px] justify-end">
        <button
          type="button"
          onClick={onShow}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <FaEye className="h-4 w-4" />
          Afficher la météo
          {city ? <span className="hidden sm:inline">de {city}</span> : null}
        </button>
      </div>
    </section>
  );
}

export default function HomeWeatherPanel() {
  const enabled = getEnv("REACT_APP_WEATHER_ENABLED", "false") === "true";

  const city = safeStr(getEnv("REACT_APP_WEATHER_CITY", "Bruges"));
  const postalCode = safeStr(getEnv("REACT_APP_WEATHER_POSTAL_CODE", "33520"));
  const latitude = getEnv("REACT_APP_WEATHER_LATITUDE", "44.8846");
  const longitude = getEnv("REACT_APP_WEATHER_LONGITUDE", "-0.6121");
  const refreshMinutes = Number(
    getEnv("REACT_APP_WEATHER_REFRESH_MINUTES", "20"),
  );

  const [weather, setWeather] = useState(() =>
    getCachedWeather(refreshMinutes),
  );
  const [loading, setLoading] = useState(!weather);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      const savedValue = localStorage.getItem(HIDDEN_KEY);

      // Par défaut, le panneau est masqué.
      if (savedValue === null) return true;

      return savedValue === "true";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!enabled || hidden) return;

    const controller = new AbortController();

    const fetchWeather = async () => {
      const cached = getCachedWeather(refreshMinutes);

      if (cached) {
        setWeather(cached);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await getPublicWeather({
          latitude,
          longitude,
          signal: controller.signal,
        });

        setWeather(response);
        setCachedWeather(response);
      } catch (err) {
        if (err?.name === "AbortError") return;

        console.error("Erreur getPublicWeather:", err);
        setError("Météo momentanément indisponible.");
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();

    return () => controller.abort();
  }, [enabled, hidden, latitude, longitude, refreshMinutes]);

  const computed = useMemo(() => {
    const current = weather?.current;
    if (!current) return null;

    const weatherCode = Number(current.weather_code);
    const meta = WEATHER_META[weatherCode] || {
      label: "Conditions météo",
      icon: FaCloudSun,
      tone: "text-sky-600",
      bg: "bg-sky-500/10",
    };

    const temp = current.temperature_2m;
    const apparent = current.apparent_temperature;
    const humidity = current.relative_humidity_2m;
    const wind = current.wind_speed_10m;
    const gusts = current.wind_gusts_10m;
    const precipitation = current.precipitation;
    const cloudCover = current.cloud_cover;

    const daily = weather?.daily || {};
    const tempMax = Array.isArray(daily.temperature_2m_max)
      ? daily.temperature_2m_max[0]
      : null;
    const tempMin = Array.isArray(daily.temperature_2m_min)
      ? daily.temperature_2m_min[0]
      : null;
    const precipitationDay = Array.isArray(daily.precipitation_sum)
      ? daily.precipitation_sum[0]
      : null;

    return {
      meta,
      Icon: meta.icon,
      temperature: `${formatNumber(temp)}°C`,
      apparent: `${formatNumber(apparent)}°C`,
      humidity: `${formatNumber(humidity)}%`,
      wind: `${formatNumber(wind)} km/h`,
      gusts: `${formatNumber(gusts)} km/h`,
      precipitation: `${formatNumber(precipitation, 1)} mm`,
      cloudCover: `${formatNumber(cloudCover)}%`,
      tempRange:
        tempMin !== null || tempMax !== null
          ? `${formatNumber(tempMin)}° / ${formatNumber(tempMax)}°`
          : "—",
      precipitationDay:
        precipitationDay !== null
          ? `${formatNumber(precipitationDay, 1)} mm`
          : "—",
      time: current.time,
      isDay: Number(current.is_day) === 1,
    };
  }, [weather]);

  const handleHide = () => {
    setHidden(true);

    try {
      localStorage.setItem(HIDDEN_KEY, "true");
    } catch {
      // LocalStorage indisponible : on ignore.
    }
  };

  const handleShow = () => {
    setHidden(false);

    try {
      localStorage.setItem(HIDDEN_KEY, "false");
    } catch {
      // LocalStorage indisponible : on ignore.
    }
  };

  if (!enabled) return null;

  if (hidden) {
    return <HiddenWeatherButton city={city} onShow={handleShow} />;
  }

  if (loading) {
    return (
      <section className="relative bg-slate-50 px-4 py-6 dark:bg-slate-950 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1380px]">
          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-950/5 dark:border-white/10 dark:bg-slate-900">
            <div className="flex animate-pulse items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-slate-100 dark:bg-white/10" />
              <div className="flex-1 space-y-3">
                <div className="h-4 w-40 rounded bg-slate-100 dark:bg-white/10" />
                <div className="h-7 w-72 max-w-full rounded bg-slate-100 dark:bg-white/10" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !computed) {
    return null;
  }

  const Icon = computed.Icon;

  return (
    <section className="relative bg-slate-50 px-4 py-6 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1380px]">
        <Reveal>
          <article className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-emerald-50 shadow-xl shadow-slate-950/5 transition duration-300 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-slate-950/10 dark:border-slate-800 dark:from-sky-950/35 dark:via-slate-900 dark:to-emerald-950/25">
            <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-sky-400/20 blur-3xl transition duration-300 group-hover:scale-125" />
            <div className="pointer-events-none absolute -bottom-20 left-20 h-44 w-44 rounded-full bg-emerald-400/15 blur-3xl" />
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/90 to-transparent dark:via-white/20" />

            <div className="relative p-5 md:p-6 lg:p-7">
              <div className="grid gap-5 lg:grid-cols-[auto_1fr_auto] lg:items-center">
                <div
                  className={`flex h-16 w-16 items-center justify-center rounded-3xl ${computed.meta.bg} ${computed.meta.tone} shadow-sm`}
                >
                  <Icon className="h-8 w-8" />
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/75 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-600 shadow-sm ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10">
                      <span className="h-2 w-2 rounded-full bg-sky-500" />
                      Météo locale
                    </span>

                    <span className="inline-flex items-center gap-2 rounded-full bg-white/60 px-3 py-1 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-400 dark:ring-white/10">
                      {city}
                      {postalCode ? ` ${postalCode}` : ""}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-2">
                    <h2 className="text-4xl font-bold leading-none text-slate-950 dark:text-white md:text-5xl">
                      {computed.temperature}
                    </h2>

                    <div className="pb-1">
                      <p className="text-lg font-bold text-slate-800 dark:text-slate-100">
                        {computed.meta.label}
                      </p>
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                        Ressenti {computed.apparent}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => setExpanded((value) => !value)}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    {expanded ? (
                      <>
                        Moins de détails
                        <FaChevronUp className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Plus de détails
                        <FaChevronDown className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleHide}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    <FaEyeSlash className="h-4 w-4" />
                    Masquer
                  </button>
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <WeatherMetric
                  icon={FaWind}
                  label="Vent"
                  value={computed.wind}
                />
                <WeatherMetric
                  icon={FaTint}
                  label="Humidité"
                  value={computed.humidity}
                />
                <WeatherMetric
                  icon={FaTemperatureHigh}
                  label="Aujourd’hui"
                  value={computed.tempRange}
                />
                <WeatherMetric
                  icon={FaCloudRain}
                  label="Pluie"
                  value={computed.precipitationDay}
                />
              </div>

              {expanded ? (
                <div className="mt-5 grid gap-3 rounded-3xl border border-slate-200 bg-white/55 p-4 backdrop-blur dark:border-white/10 dark:bg-white/[0.03] sm:grid-cols-2 lg:grid-cols-4">
                  <WeatherMetric
                    icon={FaWind}
                    label="Rafales"
                    value={computed.gusts}
                  />
                  <WeatherMetric
                    icon={FaCloud}
                    label="Couverture nuageuse"
                    value={computed.cloudCover}
                  />
                  <WeatherMetric
                    icon={FaCloudRain}
                    label="Pluie actuelle"
                    value={computed.precipitation}
                  />
                  <WeatherMetric
                    icon={computed.isDay ? FaSun : FaCloudSun}
                    label="Période"
                    value={computed.isDay ? "Journée" : "Nuit"}
                  />
                </div>
              ) : null}
            </div>

            <div className="relative border-t border-slate-200/80 bg-white/45 px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 backdrop-blur dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400 md:px-6">
              <span className="inline-flex items-center gap-2">
                <FaInfoCircle className="h-3.5 w-3.5 text-sky-500" />
                Données météo indicatives mises à jour automatiquement
              </span>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
}
